import { apiFetch, setToken, clearToken, getToken, setRefreshToken, clearRefreshToken } from './client.ts';
import type { AuthResponse, LicenceSnapshot, StrapiUser, UserRole } from './types';

/**
 * Strapi Users & Permissions auth (SPEC §4.6 / ARCHITECTURE §8): POST /api/auth/local for the
 * "kj" operator role. Registration is deliberately not implemented here — it is disabled
 * server-side (strapi-template/src/bootstrap/roles.js) and the VPS operator's account is
 * created by hand per DEPLOY.md.
 */
/**
 * MVP-471: `turnstileToken` is only ever sent when the server has already asked for one — it
 * answers a 403 `CHALLENGE_REQUIRED`, which `loginGuard` raises from the third attempt after two
 * failures for that address. A KJ signing in once a night never produces one, and this argument
 * stays undefined on every deployment that has not configured Turnstile at all.
 */
export async function login(identifier: string, password: string, turnstileToken?: string): Promise<StrapiUser> {
  const res = await apiFetch<AuthResponse>('/api/auth/local', {
    method: 'POST',
    body: { identifier, password, turnstileToken },
    auth: false, // MVP-135: a stale token in storage would turn this into a 403 'Forbidden' dead-lock
    retry: false, // never silently replay a login submit
  });
  setToken(res.jwt);
  // VPS-10: stored alongside the access token so `apiFetch` can refresh a lapsed 60-minute
  // session instead of forcing a re-login (see client.ts's own comments). Absent when the server
  // is configured with `sessions.httpOnly: true` — see AuthResponse's own comment.
  if (res.refreshToken) setRefreshToken(res.refreshToken);
  return res.user;
}

/**
 * P2 fix: `App.tsx` only ever learned the signed-in user's identity (for the header's email
 * display) from a fresh `login()` response — after a reload, the JWT survives in storage
 * (`isAuthenticated()` is `true`) but the in-memory `user` does not, so the header silently lost
 * the email until the next sign-in. `GET /api/users/me` (users-permissions, any authenticated
 * role) returns the same user shape as `login()`'s `res.user`.
 */
export async function me(): Promise<StrapiUser> {
  return apiFetch<StrapiUser>('/api/users/me');
}

interface WhoamiResponse {
  data: StrapiUser & { role: UserRole; licence?: LicenceSnapshot | null };
}

/**
 * MVP-513: what a caller gets back from `whoami()`. It used to hand back the role alone, because the
 * role was the one new thing MVP-224 needed; the licence block arrived beside it on the SAME
 * response, and a second exported function for it would have meant a second request for a payload
 * the first one already carried.
 *
 * Both members are independently nullable: `licence` is null for an account with no licence (every
 * admin, and any customer not yet on one) AND for every server that predates the block, which is why
 * nothing in web/ may read it as "there is no expiry" — only as "nothing to say".
 */
export interface WhoamiSession {
  role: UserRole | null;
  licence: LicenceSnapshot | null;
}

/**
 * MVP-224: `GET /api/session/whoami`, added by a parallel backend task specifically because
 * `GET /api/users/me` above never returns the role relation under any populate syntax on the
 * deployed Strapi (verified live). Returns just the role — `me()`/the existing `user` state
 * already cover identity, this is the one new piece of information the console needs.
 *
 * This endpoint does not exist on the server yet, so every failure mode — 404 (not deployed),
 * 401 (not signed in), 403 (no permission), or a plain network error — degrades to `null` here
 * rather than throwing. Callers must treat `null` as "unknown, no downloads", never as an error to
 * surface: this is a best-effort enhancement layered on top of a console that must keep working
 * with zero role information.
 *
 * MVP-513 widened the return from `UserRole | null` to the whole session — see `WhoamiSession`. The
 * degrade contract is unchanged and now covers both halves at once: a failure answers `null`, and a
 * console with no licence information shows no countdown, exactly as one with no role shows no
 * ingest rail.
 */
export async function whoami(): Promise<WhoamiSession | null> {
  try {
    const res = await apiFetch<WhoamiResponse>('/api/session/whoami');
    return { role: res.data.role ?? null, licence: res.data.licence ?? null };
  } catch {
    return null;
  }
}

/**
 * MVP-231: `POST /api/auth/change-password` (users-permissions, granted to every signed-in role).
 * The current password is required by the plugin, so a tab left open on someone else's machine
 * cannot take the account over.
 *
 * Strapi mints a fresh JWT as part of the change and invalidates nothing else; storing it keeps
 * this browser on the newest session instead of riding the old token to its expiry. `retry: false`
 * because a password submit must never be replayed.
 */
export async function changePassword(currentPassword: string, password: string): Promise<void> {
  const res = await apiFetch<AuthResponse>('/api/auth/change-password', {
    method: 'POST',
    body: { currentPassword, password, passwordConfirmation: password },
    retry: false,
  });
  if (res.jwt) setToken(res.jwt);
  if (res.refreshToken) setRefreshToken(res.refreshToken);
}

export function logout(): void {
  clearToken();
  clearRefreshToken();
}

export function isAuthenticated(): boolean {
  return getToken() !== null;
}
